import { useState, type CSSProperties, type ReactNode } from 'react';
import { color, itemKindTheme, meetingPhaseTheme } from '../styles/theme';
import { dateTime, initials, siteDateTime, siteDay } from '../lib/format';
import { dueLabel, dueOn } from '../lib/deadlines';
import { useRemembered } from '../lib/remembered';
import type { Meeting, MeetingItem, MeetingItemKind, MeetingPerson, MeetingPhase, MeetingRow } from '../api/client';
import { KIND_LABEL, PHASE_LABEL, STATUS_LABEL, useUpdateItem, type ItemPatch } from '../api/meetings';
import { Button, Card, CardTitle, inputStyle, selectStyle } from '../components/ui';
import { IconCheck } from '../components/icons';

/**
 * The small pieces every Meetings screen shares: the phase and kind pills,
 * a due date that stands out only when late, the people picker, one action
 * item's row, and the panel of what carried over from the last meeting.
 */

const pill: CSSProperties = {
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  padding: '3px 9px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};

export function PhasePill({ phase }: { phase: MeetingPhase }) {
  const t = meetingPhaseTheme[phase];
  return <span style={{ ...pill, background: t.bg, color: t.fg }}>{PHASE_LABEL[phase]}</span>;
}

export function KindPill({ kind }: { kind: MeetingItemKind }) {
  const t = itemKindTheme[kind];
  return <span style={{ ...pill, background: t.bg, color: t.fg }}>{KIND_LABEL[kind]}</span>;
}

/** "Sep 22, 10:00 AM EDT", or the honest blank. */
export function whenLabel(scheduledAt: string | null): string {
  return scheduledAt ? siteDateTime(scheduledAt) : 'Not scheduled yet';
}

/**
 * An action item's due date. Late and due-today are the only states that
 * stand out; a date ahead is plain text; no date says so quietly.
 */
export function DueTag({ dueOn: date, status }: { dueOn: string | null; status: MeetingItem['status'] }) {
  if (!date) {
    return (
      <span style={{ fontSize: 12.5, color: color.muted }} title="No due date yet">
        No date
      </span>
    );
  }
  const due = dueOn(date);
  if (status !== 'open' || due.state === 'upcoming') {
    return (
      <span style={{ fontSize: 12.5, color: status === 'open' ? color.ink : color.muted, whiteSpace: 'nowrap' }}>
        {dueLabel(date)}
      </span>
    );
  }
  const late = due.state === 'overdue';
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          ...pill,
          alignSelf: 'flex-start',
          background: late ? '#fdf1ee' : '#f6ddc2',
          color: late ? '#a4432f' : '#8a4f16',
        }}
      >
        {late ? 'Overdue' : 'Due today'}
      </span>
      <span style={{ fontSize: 11.5, color: color.muted, whiteSpace: 'nowrap' }}>{dueLabel(date)}</span>
    </span>
  );
}

/** Whether an open action item is overdue or due today, for counts. */
export function isLate(item: Pick<MeetingItem, 'dueOn' | 'status'>): boolean {
  return item.status === 'open' && item.dueOn !== null && dueOn(item.dueOn).state !== 'upcoming';
}

/** Initials in a row, for a list of attendees. */
export function Faces({ people, max = 5 }: { people: ReadonlyArray<{ id: string; name: string }>; max?: number }) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span style={{ display: 'flex', alignItems: 'center' }} title={people.map((p) => p.name).join(', ')}>
      {shown.map((p, i) => (
        <span
          key={p.id}
          style={{
            display: 'flex',
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: color.tint2,
            color: color.blueDeep,
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10.5,
            fontWeight: 800,
            border: `2px solid ${color.white}`,
            marginLeft: i === 0 ? 0 : -7,
          }}
        >
          {initials(p.name)}
        </span>
      ))}
      {rest > 0 && <span style={{ fontSize: 12, color: color.muted, marginLeft: 6 }}>+{rest}</span>}
    </span>
  );
}

/**
 * Who owns something: a native select over the active accounts, attendees
 * first. The current owner stays selectable even when no longer offered.
 */
export function PersonSelect({
  value,
  currentName,
  people,
  attendees,
  onChange,
  disabled,
  label,
}: {
  value: string | null;
  currentName: string | null;
  people: ReadonlyArray<MeetingPerson>;
  attendees: ReadonlyArray<{ id: string }>;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  label: string;
}) {
  const inRoom = new Set(attendees.map((a) => a.id));
  const first = people.filter((p) => inRoom.has(p.id));
  const others = people.filter((p) => !inRoom.has(p.id));
  return (
    <select
      aria-label={label}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
      style={{ ...selectStyle, fontSize: 12.5, padding: '6px 28px 6px 10px', width: 'auto', maxWidth: '100%' }}
    >
      <option value="">Unassigned</option>
      {value && !people.some((p) => p.id === value) && <option value={value}>{currentName ?? 'Current owner'}</option>}
      {first.length > 0 && (
        <optgroup label="In this meeting">
          {first.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </optgroup>
      )}
      {others.length > 0 && (
        <optgroup label="Everyone else">
          {others.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

const smallButton: CSSProperties = { padding: '5px 11px', fontSize: 12 };

/** "Weekly team meeting · Sep 22" — where a carried item came from. */
export function raisedIn(item: MeetingItem): string {
  return item.scheduledAt ? `${item.meetingTitle} · ${siteDay(item.scheduledAt)}` : item.meetingTitle;
}

/**
 * One action item, with its controls: owner, due date, and the status
 * buttons. Changes go through the meeting on screen, so closing last week's
 * item from this week's meeting records where it was settled. A wide column
 * lays the controls out as a grid under `ActionHead`; a narrow one stacks
 * them under the text.
 */
export function ActionRow({
  item,
  meetingId,
  canWrite,
  people,
  attendees,
  showFrom,
  trailing,
  layout = 'grid',
}: {
  item: MeetingItem;
  meetingId: string;
  canWrite: boolean;
  people: ReadonlyArray<MeetingPerson>;
  attendees: ReadonlyArray<{ id: string }>;
  /** Say which meeting raised it — for a carried item. */
  showFrom?: boolean;
  trailing?: ReactNode;
  layout?: 'grid' | 'stack';
}) {
  const update = useUpdateItem();
  const patch = (p: ItemPatch) => update.mutate({ id: meetingId, itemId: item.id, ...p });
  const open = item.status === 'open';
  const stacked = layout === 'stack';

  const body = (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          color: open ? color.ink : color.muted,
          textDecoration: item.status === 'dropped' ? 'line-through' : 'none',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {item.body}
      </div>
      <div style={{ fontSize: 11.5, color: color.muted, marginTop: 2 }}>
        {showFrom ? `From ${raisedIn(item)}` : item.authorName ? `Added by ${item.authorName}` : ''}
        {item.source === 'ai' ? " · from Ena's suggestion" : item.source === 'carried' ? ' · carried over' : ''}
        {!open && item.closedAt ? ` · ${STATUS_LABEL[item.status ?? 'open']} ${dateTime(item.closedAt)}` : ''}
      </div>
    </div>
  );

  const owner = (
    <div style={{ minWidth: 0 }}>
      {canWrite && open ? (
        <PersonSelect
          label={`Owner of “${item.body.slice(0, 40)}”`}
          value={item.ownerId}
          currentName={item.ownerName}
          people={people}
          attendees={attendees}
          disabled={update.isPending}
          onChange={(ownerId) => patch({ ownerId })}
        />
      ) : (
        <span style={{ fontSize: 12.5, color: item.ownerName ? color.ink : color.muted }}>{item.ownerName ?? 'Unassigned'}</span>
      )}
      {open && !item.ownerId && <div style={{ fontSize: 11, fontWeight: 800, color: '#8a4f16', marginTop: 3 }}>Needs an owner</div>}
    </div>
  );

  const due = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {canWrite && open ? (
        <input
          type="date"
          aria-label="Due date"
          value={item.dueOn ?? ''}
          disabled={update.isPending}
          onChange={(e) => patch({ dueOn: e.target.value || null })}
          style={{ ...inputStyle, width: 'auto', fontSize: 12.5, padding: '5px 8px' }}
        />
      ) : (
        <DueTag dueOn={item.dueOn} status={item.status} />
      )}
      {canWrite && open && item.dueOn && isLate(item) && <DueTag dueOn={item.dueOn} status={item.status} />}
    </div>
  );

  const status = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {canWrite ? (
        open ? (
          <>
            <Button variant="soft" style={smallButton} disabled={update.isPending} onClick={() => patch({ status: 'done' })}>
              Mark done
            </Button>
            <Button variant="ghost" style={smallButton} disabled={update.isPending} onClick={() => patch({ status: 'dropped' })}>
              Drop
            </Button>
          </>
        ) : (
          <>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: color.muted }}>{STATUS_LABEL[item.status ?? 'open']}</span>
            <Button variant="ghost" style={smallButton} disabled={update.isPending} onClick={() => patch({ status: 'open' })}>
              Reopen
            </Button>
          </>
        )
      ) : (
        <span style={{ fontSize: 12.5, fontWeight: 700, color: open ? color.ink : color.muted }}>{STATUS_LABEL[item.status ?? 'open']}</span>
      )}
      {trailing}
    </div>
  );

  const error = update.error && (
    <div role="alert" style={{ gridColumn: '1 / -1', fontSize: 12, color: color.red }}>
      {(update.error as Error).message}
    </div>
  );

  if (stacked) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 0', borderTop: `1px solid ${color.line}`, fontSize: 13.5 }}>
        {body}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {owner}
          {due}
        </div>
        {status}
        {error}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: ACTION_GRID,
        gap: 12,
        alignItems: 'center',
        padding: '10px 0',
        borderTop: `1px solid ${color.line}`,
        fontSize: 13.5,
      }}
    >
      {body}
      {owner}
      {due}
      {status}
      {error}
    </div>
  );
}

const ACTION_GRID = 'minmax(0,2fr) minmax(150px,1fr) 150px minmax(150px,1fr)';

/** The header row above a list of `ActionRow`s. */
export function ActionHead({ from }: { from?: boolean }) {
  const h: CSSProperties = { fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.muted };
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: ACTION_GRID,
        gap: 12,
        padding: '4px 0 8px',
      }}
    >
      <span style={h}>{from ? 'Action item · from' : 'Action item'}</span>
      <span style={h}>Owner</span>
      <span style={h}>Due</span>
      <span style={h}>Status</span>
    </div>
  );
}

/** A folded group: "Done or dropped last time (2)" that opens on a click. */
export function Folded({ label, count, children }: { label: string; count: number; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: color.blue }}
      >
        {open ? 'Hide' : 'Show'} {label} ({count})
      </button>
      {open && <div style={{ marginTop: 6 }}>{children}</div>}
    </div>
  );
}

/**
 * What carried over from the meeting this one continues: action items to
 * settle, questions still open, and last time's takeaways for context. Each
 * line can go on this meeting's agenda while it is being prepared.
 */
export function CarriedPanel({
  meeting,
  carried,
  canWrite,
  people,
  attendees,
  onAddToAgenda,
  compact = false,
}: {
  meeting: Meeting;
  carried: { actions: MeetingItem[]; questions: MeetingItem[]; takeaways: MeetingItem[] };
  canWrite: boolean;
  people: ReadonlyArray<MeetingPerson>;
  attendees: ReadonlyArray<{ id: string }>;
  /** Offered while the agenda is still open. */
  onAddToAgenda?: (item: MeetingItem) => void;
  compact?: boolean;
}) {
  const update = useUpdateItem();
  const openActions = carried.actions.filter((a) => a.status === 'open');
  const settled = carried.actions.filter((a) => a.status !== 'open');
  const openQuestions = carried.questions.filter((q) => !q.answeredAt);
  const answered = carried.questions.filter((q) => q.answeredAt);
  const total = carried.actions.length + carried.questions.length + carried.takeaways.length;

  const addButton = (item: MeetingItem) =>
    onAddToAgenda && canWrite ? (
      <Button variant="ghost" style={smallButton} onClick={() => onAddToAgenda(item)} title="Put this on the agenda">
        Add to agenda
      </Button>
    ) : null;

  return (
    <Card pad={compact ? 16 : 20}>
      <CardTitle style={{ marginBottom: 2 }}>Carried over</CardTitle>
      <p style={{ fontSize: 12.5, color: color.muted, margin: '0 0 10px' }}>
        From the meeting this one continues. Settling an item here settles it there too.
      </p>
      {total === 0 && <p style={{ fontSize: 13, color: color.muted, margin: 0 }}>Nothing was left open.</p>}

      {carried.actions.length > 0 && (
        <section style={{ marginBottom: 14 }}>
          <h4 style={sectionHead}>Action items</h4>
          {openActions.length === 0 && <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>None still open.</p>}
          {openActions.map((a) => (
            <ActionRow
              key={a.id}
              item={a}
              meetingId={meeting.id}
              canWrite={canWrite}
              people={people}
              attendees={attendees}
              showFrom
              trailing={addButton(a)}
              layout="stack"
            />
          ))}
          <Folded label="done or dropped" count={settled.length}>
            {settled.map((a) => (
              <ActionRow key={a.id} item={a} meetingId={meeting.id} canWrite={canWrite} people={people} attendees={attendees} showFrom layout="stack" />
            ))}
          </Folded>
        </section>
      )}

      {carried.questions.length > 0 && (
        <section style={{ marginBottom: 14 }}>
          <h4 style={sectionHead}>Questions</h4>
          {openQuestions.length === 0 && <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>All answered.</p>}
          {openQuestions.map((q) => (
            <div key={q.id} style={lineRow}>
              <div style={{ minWidth: 0 }}>
                <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{q.body}</div>
                <div style={{ fontSize: 11.5, color: color.muted }}>From {raisedIn(q)}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {canWrite && (
                  <Button variant="soft" style={smallButton} disabled={update.isPending} onClick={() => update.mutate({ id: meeting.id, itemId: q.id, answered: true })}>
                    Mark answered
                  </Button>
                )}
                {addButton(q)}
              </div>
            </div>
          ))}
          <Folded label="answered" count={answered.length}>
            {answered.map((q) => (
              <div key={q.id} style={lineRow}>
                <div style={{ minWidth: 0, color: color.muted }}>
                  <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{q.body}</div>
                  <div style={{ fontSize: 11.5 }}>Answered {q.answeredAt ? dateTime(q.answeredAt) : ''}</div>
                </div>
                {canWrite && (
                  <Button variant="ghost" style={smallButton} disabled={update.isPending} onClick={() => update.mutate({ id: meeting.id, itemId: q.id, answered: false })}>
                    Reopen
                  </Button>
                )}
              </div>
            ))}
          </Folded>
        </section>
      )}

      {carried.takeaways.length > 0 && (
        <section>
          <h4 style={sectionHead}>Takeaways last time</h4>
          {carried.takeaways.map((t) => (
            <div key={t.id} style={lineRow}>
              <div style={{ minWidth: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{t.body}</div>
              {addButton(t)}
            </div>
          ))}
        </section>
      )}
      {update.error && <p style={{ fontSize: 12, color: color.red, margin: '8px 0 0' }}>{(update.error as Error).message}</p>}
    </Card>
  );
}

const sectionHead: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: color.muted,
  margin: '0 0 6px',
};

const lineRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0,1fr) auto',
  gap: 12,
  alignItems: 'center',
  padding: '8px 0',
  borderTop: `1px solid ${color.line}`,
  fontSize: 13.5,
};

/**
 * An "About" card that stays closed once closed. A screen has to teach
 * once, then stay out of the way.
 */
export function AboutPanel({ storageKey, title, children }: { storageKey: string; title: string; children: ReactNode }) {
  const [open, setOpen] = useRemembered(storageKey, true);
  return (
    <Card pad={open ? '14px 20px' : '10px 20px'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 13, color: color.navy, flex: 1 }}>{title}</strong>
        <button
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: color.blue }}
        >
          {open ? 'Close' : 'About'}
        </button>
      </div>
      {open && <div style={{ fontSize: 13, color: color.ink, lineHeight: '20px', marginTop: 6 }}>{children}</div>}
    </Card>
  );
}

/** What a New meeting dialog needs to know about the meeting it continues. */
export interface ContinueSource {
  id: string;
  title: string;
  phase: MeetingPhase;
  scheduledAt: string | null;
  attendeeIds: string[];
  carry: { actions: number; takeaways: number; questions: number };
}

export function sourceFromRow(r: MeetingRow): ContinueSource {
  return {
    id: r.id,
    title: r.title,
    phase: r.phase,
    scheduledAt: r.scheduledAt,
    attendeeIds: r.attendees.map((a) => a.id),
    carry: { actions: r.counts.actionsOpen, takeaways: r.counts.takeaways, questions: r.counts.questionsOpen },
  };
}

/* ───────────────────────────────────────────────────────────── the steps */

type StepState = 'behind' | 'current' | 'ahead';

const STEP_ORDER: Record<MeetingPhase, number> = { preparing: 0, in_meeting: 1, done: 2 };

/** The line under each step: what has happened there, or what happens next. */
const STEP_SUPPORT: Record<MeetingPhase, (m: Meeting, items: ReadonlyArray<MeetingItem>) => string> = {
  preparing: (_m, items) => {
    const n = items.filter((i) => i.kind === 'agenda').length;
    return n === 0 ? 'Build the agenda' : `${n} agenda item${n === 1 ? '' : 's'}`;
  },
  in_meeting: (_m, items) => {
    const n = items.filter((i) => i.kind !== 'agenda').length;
    return n === 0 ? 'Notes in the room' : `${n} line${n === 1 ? '' : 's'} written`;
  },
  done: (m) =>
    m.sentAt ? `Summary sent ${siteDay(m.sentAt)}` : m.summary?.trim() ? 'Summary written, not sent' : 'Summary and send',
};

function stepCircle(state: StepState): CSSProperties {
  const base: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    borderRadius: '50%',
    fontSize: 12,
    fontWeight: 800,
    flex: 'none',
    boxSizing: 'border-box',
  };
  if (state === 'behind') return { ...base, background: color.blue, color: color.white, border: `2px solid ${color.blue}` };
  if (state === 'current') {
    return { ...base, background: color.white, color: color.blueDeep, border: `2px solid ${color.blue}`, boxShadow: `0 0 0 4px ${color.tint2}` };
  }
  return { ...base, background: color.white, color: color.muted, border: `2px solid ${color.line}` };
}

/**
 * Where the meeting is: three steps, the ones behind ticked, the current one
 * ringed, the ones ahead numbered — so a person opening the page knows what
 * has happened and what comes next without reading the About panel.
 */
export function PhaseSteps({ meeting, items }: { meeting: Meeting; items: ReadonlyArray<MeetingItem> }) {
  const at = STEP_ORDER[meeting.phase];
  const phases: MeetingPhase[] = ['preparing', 'in_meeting', 'done'];
  const line = (on: boolean, hidden: boolean): CSSProperties => ({
    flex: 1,
    height: 2,
    background: hidden ? 'transparent' : on ? color.blue : color.line,
  });
  return (
    <ol aria-label="Where this meeting is" style={{ display: 'flex', listStyle: 'none', margin: 0, padding: 0 }}>
      {phases.map((phase, i) => {
        const state: StepState = i < at ? 'behind' : i === at ? 'current' : 'ahead';
        return (
          <li
            key={phase}
            aria-current={state === 'current' ? 'step' : undefined}
            style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <span style={line(i <= at, i === 0)} />
              <span style={stepCircle(state)}>{state === 'behind' ? <IconCheck size={14} /> : String(i + 1).padStart(2, '0')}</span>
              <span style={line(i < at, i === phases.length - 1)} />
            </div>
            <span style={{ textAlign: 'center', minWidth: 0, padding: '0 6px' }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: state === 'ahead' ? 600 : 800,
                  color: state === 'ahead' ? color.muted : color.navy,
                }}
              >
                {PHASE_LABEL[phase]}
              </span>
              <span style={{ display: 'block', fontSize: 11.5, color: color.muted, marginTop: 1 }}>
                {STEP_SUPPORT[phase](meeting, items)}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
